// Utilidades para generación de eventos de calendario

/**
 * Genera un archivo .ics para agregar evento a calendarios (Google, Outlook, Apple)
 * @param {Object} eventData - Datos del evento
 * @returns {string} - Contenido del archivo .ics
 */
export const generateICSFile = (eventData) => {
  const {
    title,
    description,
    location,
    startDate,
    endDate,
    organizerEmail,
    organizerName,
    attendeeEmail,
    attendeeName,
    meetLink
  } = eventData;

  // Formatear fechas para formato iCalendar (YYYYMMDDTHHMMSS)
  const formatDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Hospital Clínico Veterinario UDLA//ES
CALSCALE:GREGORIAN
METHOD:REQUEST
BEGIN:VEVENT
UID:${Date.now()}@hcv-udla.cl
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${title}
DESCRIPTION:${description}${meetLink ? `\\n\\nUnirse a la videollamada: ${meetLink}` : ''}
LOCATION:${location || 'Videollamada'}
STATUS:CONFIRMED
SEQUENCE:0
ORGANIZER;CN=${organizerName}:mailto:${organizerEmail}
ATTENDEE;CN=${attendeeName};RSVP=TRUE:mailto:${attendeeEmail}
BEGIN:VALARM
TRIGGER:-PT1H
ACTION:DISPLAY
DESCRIPTION:Recordatorio: ${title} en 1 hora
END:VALARM
BEGIN:VALARM
TRIGGER:-PT15M
ACTION:DISPLAY
DESCRIPTION:Recordatorio: ${title} en 15 minutos
END:VALARM
END:VEVENT
END:VCALENDAR`;

  return icsContent;
};

/**
 * Descarga un archivo .ics
 * @param {string} icsContent - Contenido del archivo .ics
 * @param {string} filename - Nombre del archivo
 */
export const downloadICSFile = (icsContent, filename = 'evento.ics') => {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Genera un link de Google Calendar para agregar evento
 * @param {Object} eventData - Datos del evento
 * @returns {string} - URL de Google Calendar
 */
export const generateGoogleCalendarLink = (eventData) => {
  const {
    title,
    description,
    location,
    startDate,
    endDate,
    meetLink
  } = eventData;

  // Formato: YYYYMMDDTHHmmssZ
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    details: `${description}\n\n${meetLink ? `Unirse a la videollamada:\n${meetLink}` : ''}`,
    location: location || 'Videollamada',
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    sf: 'true',
    output: 'xml'
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Genera template de email para teleconsulta
 * @param {Object} emailData - Datos para el email
 * @returns {Object} - Subject y body del email
 */
export const generateEmailTemplate = (emailData) => {
  const {
    tutorName,
    patientName,
    date,
    time,
    duration,
    veterinarianName,
    reason,
    meetLink,
    notes
  } = emailData;

  const subject = `Teleconsulta Agendada - ${patientName}`;

  const body = `Estimado/a ${tutorName},

Su teleconsulta ha sido agendada exitosamente.

📅 DETALLES DE LA CITA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Fecha: ${date}
Hora: ${time} hrs
Duración: ${duration} minutos
Veterinario/a: ${veterinarianName}
Paciente: ${patientName}
Motivo: ${reason}

🎥 VIDEOLLAMADA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Para unirse a la teleconsulta, haga clic en el siguiente enlace:
${meetLink}

📝 INSTRUCCIONES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Asegúrese de tener buena conexión a internet
• Use Chrome, Firefox o Edge para mejor experiencia
• Tenga a su mascota cerca durante la consulta
• Prepare cualquier pregunta o documentación que desee compartir
• Únase 5 minutos antes de la hora programada
${notes ? `\n📌 NOTAS ADICIONALES:\n${notes}\n` : ''}
📞 CONTACTO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Si necesita cambiar la cita o tiene alguna consulta, por favor contáctenos.

Saludos cordiales,
Hospital Clínico Veterinario UDLA

---
Este es un correo automático, por favor no responder.`;

  return { subject, body };
};

/**
 * Copia texto al portapapeles
 * @param {string} text - Texto a copiar
 * @returns {Promise<boolean>} - True si se copió exitosamente
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Error copying to clipboard:', err);
    return false;
  }
};
